/**
 * TOSS Web Application - User Avatar Component
 *
 * Displays user avatar with photo or initials fallback.
 * Reusable across the application for consistent user representation.
 *
 * **The fallback is quiet (28.08.2026).** A group used to be a solid brand-blue disc and a person a
 * lilac one, which made the loudest colour on the screen belong to the thing that has nothing to
 * say: «эти заглушки очень яркие и забирают на себя внимание». Grey disc, grey glyph, grey
 * initials. Brand stays where it marks a STATE — the unread badge, the send button, the selected
 * row — and a real photo is unaffected either way.
 */

import { useState } from 'react';
import { User, Users } from 'lucide-react';
import { getFileUrl } from '@/api/client';

// ============================================================================
// Types
// ============================================================================

export type UserAvatarSize = 'xs' | 'sm' | 'md' | 'lg';

export interface UserAvatarProps {
  /** URL to user's photo */
  photoUrl?: string | null;
  /** User's full name for initials */
  fullName?: string;
  /** Avatar size */
  size?: UserAvatarSize;
  /** Show border (for use on dark backgrounds) */
  showBorder?: boolean;
  /** Additional CSS classes */
  className?: string;
  /**
   * Variant:
   * - 'user' (default) — shows initials or UserIcon fallback
   * - 'group' — shows UsersIcon as fallback (for group chats / teams)
   */
  variant?: 'user' | 'group';
}

// ============================================================================
// Constants
// ============================================================================

const sizeClasses: Record<UserAvatarSize, string> = {
  xs: 'w-6 h-6 text-3xs',
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-12 h-12 text-base',
};

const iconSizes: Record<UserAvatarSize, string> = {
  xs: 'w-3 h-3',
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
};

// ============================================================================
// Component
// ============================================================================

/**
 * User avatar component displaying photo or initials fallback
 */
export function UserAvatar({
  photoUrl,
  fullName,
  size = 'md',
  showBorder = false,
  className = '',
  variant = 'user',
}: UserAvatarProps): JSX.Element {
  // Фото, которое не загрузилось, должно уступить место заглушке, а не оставлять сломанную
  // иконку: `PhotoUrl` в базе живёт дольше файла на диске, и до 07.09.2026 загрузка аватара
  // вообще не сохраняла байты — путь был записан, а файла не существовало.
  const [hasError, setHasError] = useState(false);

  const initials = fullName
    ? fullName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : '';

  const borderClass = showBorder ? 'border-2 border-white' : '';
  const resolvedPhoto = photoUrl ? getFileUrl(photoUrl) : undefined;

  if (resolvedPhoto && !hasError) {
    return (
      <img
        src={resolvedPhoto}
        alt={fullName || 'User avatar'}
        className={`${sizeClasses[size]} rounded-full object-cover ${borderClass} ${className}`}
        onError={() => setHasError(true)}
      />
    );
  }

  // Group variant: show UsersIcon when no photo, ignore initials
  if (variant === 'group') {
    return (
      <div
        className={`
          ${sizeClasses[size]}
          rounded-full bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]
          flex items-center justify-center
          ${borderClass}
          ${className}
        `}
      >
        <Users className={iconSizes[size]} />
      </div>
    );
  }

  return (
    <div
      className={`
        ${sizeClasses[size]}
        rounded-full bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]
        flex items-center justify-center font-medium
        ${borderClass}
        ${className}
      `}
    >
      {initials || <User className={iconSizes[size]} />}
    </div>
  );
}

export default UserAvatar;
